/**
 * Copyright (c) 2022 FMR LLC.
 * All Rights Reserved.
 * Fidelity Confidential Information.
 */

import { ClientConstants } from './../client.constants';
import { ContentComponentsModel } from './../models/content-model';

export class Utils {
    /**
     * @description processes content with a HOST that needs to be replaced. the text passed in should be between two "~"s.
     * @param {string} text text to parse
     * @param {string} host host text to insert
     * @returns {string} text with replaced host
     */
    public static processHost(text?: string, host?: string): string {
        return text?.replace(/~.*~/, host ?? '') ?? '';
    }

    /**
     * @description processes an img tag that needs a host for the src property
     * @param {string} text text to parse
     * @param {string} host host text to insert
     * @returns {string} img tag with new host
     */
    public static processImgTag(text?: string, host?: string): string {
        const regex = new RegExp('src="/bin-public', 'g');
        const replacement = `src="${host}/bin-public`;
        return text?.replace(regex, replacement) ?? '';
    }

    /**
     * @description calculate the fraction of a year based on start and end dates using ACTUAL/ACTUAL
     * @param start starting date
     * @param end ending date
     * @returns fraction of year
     */
    public static calculateFractionOfYear(start: string, end: string): number {
        const startDate = new Date(start);
        const endDate = new Date(end);

        const millisecondsInDay = 24 * 60 * 60 * 1000;
        const dayDiff = Math.round(Math.abs(endDate.getTime() - startDate.getTime()) / millisecondsInDay);
        return dayDiff / 365;
    }

    /**
     * @description calculate the return amount for a CD that is held to maturity baesd on the yield, dated date,
     * and maturity date
     * @param investmentAmount the amount being invested in the CD
     * @param rate the CD yield
     * @param datedDate the CD dated date
     * @param maturityDate  the CD maturity date
     * @returns return amount if the CD is held to maturity
     */
    public static calculateReturnAmount(
        investmentAmount: number,
        rate: number,
        datedDate: string,
        maturityDate: string
    ): number {
        const fractionOfYear = Utils.calculateFractionOfYear(datedDate, maturityDate);
        let returnAmount = investmentAmount * (rate / 100) * fractionOfYear;

        // The returnAmount is truncated.  Add a tolerance value to handle situations when an
        // amount is not exactly represented, for example, 41.00 is calculated as 40.99999999999999.
        returnAmount += 0.0000000001;
        return Math.trunc(returnAmount * 100) / 100;
    }

    /**
     * @description formats a date like this, "Jan 1, 2022, 3:45 AM EST"
     * @param dateString a string containing a date in some other format
     * @returns a date string with this format: MMM DD, YYYY, HH:MM AM|PM EDT|EST
     */
    public static formatTimestamp(dateString: string | undefined): string {
        if (dateString && !isNaN(new Date(dateString).getMinutes())) {
            const options = {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                timeZone: 'America/New_York',
                timeZoneName: 'short'
            } as const;
            let formattedDate = new Intl.DateTimeFormat('en-US', options).format(new Date(dateString));
            formattedDate = formattedDate.replace('EDT', 'ET').replace('EST', 'ET');
            return formattedDate;
        }
        return '';
    }

    /**
     * @description formats a date like this, "Jan 1, 2022"
     * @param dateString a string containing a date in this format, 'YYYY-MM-DD and is assumed to be ET
     * @returns a date string with this format: MMM DD, YYYY
     */
    public static formatMaturityDate(dateString: string | undefined): string {
        const dateReg = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateString || !dateReg.test(dateString)) {
            return '';
        }

        // When a date is created, the default time zone is UTC.
        // However, the maturity dates from the server are assumed to be ET (there is no time info in the string).
        // So when we create the date object for the maturity date, we need to force the time zone to ET.
        // Otherwise, when we format the date back to a string, we will be a day behind the actual maturity date.
        const datetimeString = dateString + 'T00:00:00.000-05:00';

        if (new Date(datetimeString).toString() !== 'Invalid Date') {
            const options = {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                timeZone: 'America/New_York'
            } as const;
            return new Intl.DateTimeFormat('en-US', options).format(new Date(datetimeString));
        }
        return '';
    }

    /**
     * @description formats a comma included number
     * @param amount the number to be formatted
     * @returns a number without commas
     */
    public static formatNumber(amount: string | number): number {
        if (typeof amount == 'string') {
            const formattedNumber = amount.toString().split(',').join('');
            return Number(formattedNumber);
        }
        return amount;
    }

    /**
     * @description build a search URL path for a set of whole CDs
     * @param {string} cdSearchPath CD search path text
     * @param {string} minMaturityDate minimum matrurity date for search
     * @param {string} maxMaturityDate maximum matrurity date for search
     * @returns {string} search URL
     */
    public static buildWholeCDSearchURL(cdSearchURL: string, minMaturityDate: Date, maxMaturityDate: Date) {
        const minFullDate = new Date(minMaturityDate);
        const maxFullDate = new Date(maxMaturityDate);
        const minYear = minFullDate.getFullYear();
        const minMonth = String(minFullDate.getMonth() + 1).padStart(2, '0)');
        const minDate = String(minFullDate.getDate()).padStart(2, '0)');
        const maxYear = maxFullDate.getFullYear();
        const maxMonth = String(maxFullDate.getMonth() + 1).padStart(2, '0');
        const maxDate = String(maxFullDate.getDate()).padStart(2, '0)');
        const searchURL = `${cdSearchURL}?name=CD&sortby=YD&minmaturity=${minDate}%2f${minMonth}%2f${minYear}&maxmaturity=${maxDate}%2f${maxMonth}%2f${maxYear}`;
        return searchURL;
    }

    /**
     * @description build a search URL path for a specific fractional CD
     * @param {string} cdSearchPath CD search path text
     * @param {string} cusip fractional cusip to search
     * @returns {string} search URL
     */
    public static buildFractionalCDSearchURL(cdSearchURL: string, cusip: string) {
        const searchURL = `${cdSearchURL}?name=CD&cusip=${cusip}`;
        return searchURL;
    }

    /**
     * @description replace a tooltip placeholder with html for the actual tooltip
     * @param {string} content the content containing the tooltip placeholder
     * @param {string} key the trigger text in the tooltip placeholder - identifies which one is being replaced
     * @param {string} title the title to put in the tooltip box
     * @param {string} text the text to put in the tooltip box
     * @returns {string} the updated content
     */
    public static fillInTooltip(content: string, key?: string, title?: string, text?: string): string {
        if (!key || !title || !text) {
            return content;
        }

        // find all tooltip placeholders in this content
        const regex = /<a href="tooltip:\/\/\?title=&size=\d+&focus=false&icon=">[^<]+<\/a>/g;
        const matches = content.match(regex);

        // if any of the tooltips match the key, replace the html
        let updatedContent = content;
        matches?.forEach(match => {
            if (match.includes('>' + key + '<')) {
                updatedContent = updatedContent.replace(match, Utils.tooltip(key, title, text));
            }
        });

        return updatedContent;
    }

    private static tooltip(key: string, title: string, text: string): string {
        return (
            '<pvd3-tooltip pvd-disable-trigger-slot-button pvd-a11y-announce-trigger>' +
            '<span class="pvd-tooltip__tooltip-content-reset" style="font-weight: 700;">' +
            title +
            '</span>' +
            '<br /><br />' +
            '<span class="pvd-tooltip__tooltip-content-reset">' +
            text +
            '</span>' +
            '<pvd3-link slot="trigger" pvd-trigger pvd-aria-label="' +
            key +
            '">' +
            key +
            '</pvd3-link>' +
            '</pvd3-tooltip>'
        );
    }

    /**
     * @description maps content with the appropriate sourceId
     * @param {string} content the content response
     * @param {string} id the id to map to
     * @returns {ContentComponentsModel} the mapped content
     */
    public static mapContent(contentComponents: ContentComponentsModel[], id: string): ContentComponentsModel {
        const mappedContent = contentComponents.find(data => {
            return data.sourceId === id;
        });

        return mappedContent ?? {};
    }

    /**
     * @description sends tracking call to adobe analytics. Only use intname param if your are using a interaction call
     * @param {string} pname name of page being tacked
     * @param {string} intname OPTIONAL: name of interaction being tracked
     */
    public static trackingFunction(pname: string, intname?: string) {
        const trackedTags =
            intname === undefined
                ? { ...ClientConstants.TAG_TEMPLATE, pname }
                : { ...ClientConstants.TAG_TEMPLATE, pname, intname, action: 'interaction' };
        const bootstrapper = window['Bootstrapper' as any] as any;
        if (bootstrapper !== undefined) {
            const trackingAnalytics = bootstrapper._trackAnalytics;
            if (trackingAnalytics !== undefined && typeof trackingAnalytics === 'function') {
                trackingAnalytics(trackedTags);
            } else {
                console.error('_trackedAnalytics is not a function');
            }
        } else {
            console.error('Bootstrapper was not found');
        }
        return undefined;
    }
}
